import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import type { BulkConfirmItem, BulkConfirmResponse, ProposalHeader } from '../../api/types'
import { useStores } from '../../app/StoreContext'
import { Badge, Button, Card, Empty, ErrorBox, Num, Spinner, formatDate, plural } from '../../ui/primitives'
import { GridHead, GridSummary, useGrid, type ColumnDef } from '../../ui/grid'
import { channelHint, channelLabel, formatPacks } from './shared'

/**
 * Approvazione in blocco delle proposte d'ordine.
 *
 * L'elenco contiene solo le proposte che hanno raggiunto il minimo d'ordine, ed è il server a
 * garantirlo (`aboveMinimum=true`): chi approva non deve fidarsi di un filtro di interfaccia.
 * La conferma in blocco ha lo stesso effetto della conferma singola, proposta per proposta:
 * quelle che nel frattempo non sono più confermabili restano aperte, con i motivi a video.
 */
export function BulkReviewPage() {
  const { selected: selectedStores, stores, storeFilter, storesReady, storeName } = useStores()
  const navigate = useNavigate()
  const storeKey = selectedStores.join(',')

  const [proposals, setProposals] = useState<ProposalHeader[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()
  const [reloadToken, setReloadToken] = useState(0)

  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set())
  const [confirming, setConfirming] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [outcome, setOutcome] = useState<BulkConfirmResponse & { verb: string }>()

  // Per confermare servono le proposte sopra il minimo; per ripulire l'elenco servono tutte.
  const [onlyAboveMinimum, setOnlyAboveMinimum] = useState(true)

  useEffect(() => {
    let cancelled = false
    if (!storesReady) return

    setLoading(true)
    setError(undefined)

    // Niente allineamento col motore qui: l'approvazione in blocco lavora su ciò che è già in
    // archivio, e comunque ogni conferma riverifica la proposta al momento in cui parte.
    api
      .get<ProposalHeader[]>('/order-proposals', {
        storeCode: storeFilter,
        status: 'Open',
        sync: false,
        aboveMinimum: onlyAboveMinimum,
      })
      .then((data) => {
        if (cancelled) return
        setProposals(data)
        setChecked(new Set())
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e)
        setProposals(undefined)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, storesReady, reloadToken, onlyAboveMinimum])

  const rows = proposals ?? []

  const columns = useMemo<ColumnDef<ProposalHeader>[]>(
    () => [
      { key: 'pick', label: '', plain: true, value: () => null },
      { key: 'store', label: 'Punto vendita', value: (p) => `${p.store} ${storeName(p.store)}` },
      { key: 'supplier', label: 'Fornitore', value: (p) => `${p.supplier} ${p.supplierName}` },
      { key: 'channel', label: 'Canale', value: (p) => channelLabel(p.channel) },
      { key: 'forecast', label: 'Data previsione', value: (p) => p.forecastDate },
      { key: 'lines', label: 'Righe', value: (p) => p.lineCount, numeric: true },
      { key: 'packs', label: 'Colli', value: (p) => p.totalPacks, numeric: true },
      { key: 'threshold', label: 'Minimo d’ordine', value: (p) => p.thresholdPercent, numeric: true },
    ],
    [storeName],
  )

  const grid = useGrid(rows, columns)

  // «Seleziona tutti» prende i soli filtrati e visibili, mai l'intero archivio: se il filtro
  // nasconde delle righe va detto con un numero, non lasciato intuire.
  const shownIds = useMemo(() => grid.rows.map((p) => p.id), [grid.rows])
  const allShownChecked = shownIds.length > 0 && shownIds.every((id) => checked.has(id))

  const toggleAllShown = () =>
    setChecked((current) => {
      const next = new Set(current)
      if (allShownChecked) shownIds.forEach((id) => next.delete(id))
      else shownIds.forEach((id) => next.add(id))
      return next
    })

  const toggleOne = (id: number) =>
    setChecked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const runBulk = async (
    path: string,
    body: Record<string, unknown>,
    verb: string,
    setBusy: (v: boolean) => void,
  ) => {
    setBusy(true)
    setOutcome(undefined)
    setError(undefined)
    try {
      const result = await api.post<BulkConfirmResponse>(path, { ids: [...checked], ...body })
      setOutcome({ ...result, verb })
      // Le riuscite spariscono dall'elenco; le fallite restano, coi motivi accanto.
      const failed = new Set(result.results.filter((r) => !r.confirmed).map((r) => r.id))
      setProposals((current) => current?.filter((p) => failed.has(p.id) || !checked.has(p.id)))
      setChecked(new Set())
    } catch (e: unknown) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  const confirmChecked = () =>
    runBulk('/order-proposals/bulk-confirm', {}, 'confermate e trasmesse', setConfirming)

  const discardChecked = () => {
    // La motivazione è obbligatoria ed è l'unica traccia che resta della decisione.
    const reason = window.prompt(
      `Scartare ${plural(checked.size, 'proposta', 'proposte')}?\n\nMotivazione (vale per tutte):`,
    )
    if (reason === null) return
    if (reason.trim().length < 3) {
      setError(new Error('La motivazione serve: almeno tre caratteri.'))
      return
    }
    void runBulk('/order-proposals/bulk-discard', { reason: reason.trim() }, 'scartate', setDiscarding)
  }

  const problemsById = useMemo(() => {
    const map = new Map<number, BulkConfirmItem>()
    outcome?.results.forEach((r) => {
      if (!r.confirmed) map.set(r.id, r)
    })
    return map
  }, [outcome])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Approvazione in blocco</h1>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            {onlyAboveMinimum ?
              'Solo proposte che hanno raggiunto il minimo d’ordine'
            : 'Tutte le proposte aperte'}
            {selectedStores.length > 0 &&
              ` · ${plural(selectedStores.length, 'punto vendita selezionato', 'punti vendita selezionati')}`}
          </p>
        </div>
        <div className="page-tools">
          {/* Due platee, due gesti: sopra il minimo per confermare, tutte per ripulire. */}
          <Button
            variant={onlyAboveMinimum ? 'primary' : 'default'}
            onClick={() => setOnlyAboveMinimum(true)}
          >
            Sopra il minimo
          </Button>
          <Button
            variant={onlyAboveMinimum ? 'default' : 'primary'}
            onClick={() => setOnlyAboveMinimum(false)}
          >
            Tutte le aperte
          </Button>
          <Button onClick={() => navigate('/proposals')}>Torna all’elenco completo</Button>
          <Button
            onClick={() => setReloadToken((t) => t + 1)}
            disabled={loading || confirming || discarding}
          >
            Aggiorna
          </Button>
        </div>
      </div>

      {outcome && (
        <div className={`notice ${outcome.failed === 0 ? 'notice-info' : 'notice-warn'}`}>
          <strong>
            {outcome.confirmed} {outcome.confirmed === 1 ? 'proposta' : 'proposte'} {outcome.verb}
            {outcome.failed > 0 && `, ${outcome.failed} no`}
          </strong>
          {outcome.failed > 0 && (
            <p>Le proposte non riuscite restano in elenco, con i motivi accanto a ciascuna.</p>
          )}
        </div>
      )}

      <Card
        title={onlyAboveMinimum ? 'Proposte pronte alla conferma' : 'Proposte aperte'}
        actions={
          <div className="inline">
            <Button onClick={toggleAllShown} disabled={loading || shownIds.length === 0}>
              {allShownChecked ? 'Deseleziona' : 'Seleziona'} {shownIds.length === 1 ? 'l’unica visibile' : `tutte le ${shownIds.length} visibili`}
            </Button>
            <Button
              variant="danger"
              onClick={discardChecked}
              disabled={checked.size === 0 || confirming || discarding || loading}
              title="Scarta con motivazione: le proposte restano consultabili, non spariscono"
            >
              {discarding ?
                'Scarto in corso…'
              : checked.size === 0 ?
                'Scarta le selezionate'
              : `Scarta ${plural(checked.size, 'proposta', 'proposte')}`}
            </Button>
            <Button
              variant="primary"
              onClick={() => void confirmChecked()}
              disabled={checked.size === 0 || confirming || discarding || loading}
            >
              {confirming ?
                'Conferma in corso…'
              : checked.size === 0 ?
                'Conferma le selezionate'
              : `Conferma ${plural(checked.size, 'proposta', 'proposte')}`}
            </Button>
          </div>
        }
      >
        {/* Il conteggio va detto a chiare lettere: se il filtro trova più righe di quante
            se ne vedono, chi preme «seleziona tutti» deve sapere cosa sta prendendo. */}
        {!loading && grid.hiddenCount > 0 && (
          <div className="notice notice-warn">
            <strong>
              {plural(grid.hiddenCount, 'proposta nascosta dal filtro', 'proposte nascoste dal filtro')}.
            </strong>
            <p>«Seleziona tutte» riguarda le sole {grid.rows.length} righe visibili: le nascoste restano fuori.</p>
          </div>
        )}

        {loading && <Spinner label="Caricamento…" />}
        {!loading && error !== undefined && <ErrorBox error={error} onRetry={() => setReloadToken((t) => t + 1)} />}

        {!loading && error === undefined && rows.length === 0 && (
          <Empty>
            {onlyAboveMinimum ?
              'Nessuna proposta ha raggiunto il minimo d’ordine'
            : 'Nessuna proposta aperta'}
            {selectedStores.length > 0 ? ' nei punti vendita selezionati' : stores.length > 1 ? ' nei punti vendita visibili' : ''}.
          </Empty>
        )}

        {!loading && error === undefined && rows.length > 0 && (
          <div className="table-wrap">
            <table>
              <GridHead columns={columns} grid={grid} />
              <tbody>
                {grid.rows.map((proposal) => {
                  const failure = problemsById.get(proposal.id)
                  return (
                    <tr key={proposal.id} className={failure ? 'row-warn' : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          checked={checked.has(proposal.id)}
                          onChange={() => toggleOne(proposal.id)}
                          aria-label={`Seleziona la proposta ${proposal.supplierName} per il punto vendita ${proposal.store}`}
                        />
                      </td>
                      <td>
                        <div className="store-code">{proposal.store}</div>
                        <div className="muted">{storeName(proposal.store)}</div>
                      </td>
                      <td>
                        <div>{proposal.supplierName}</div>
                        <div className="muted">{proposal.supplier}</div>
                        {failure && (
                          <div className="muted" style={{ color: 'var(--warn-strong, inherit)' }}>
                            {failure.problems.join(' · ')}
                          </div>
                        )}
                      </td>
                      <td title={channelHint(proposal.channel)}>
                        <Badge tone={proposal.channel === 'Cedi' ? 'info' : 'neutral'}>
                          {channelLabel(proposal.channel, proposal.sourceCedi)}
                        </Badge>
                      </td>
                      <td>{formatDate(proposal.forecastDate)}</td>
                      <td className="num">
                        <Num value={proposal.lineCount} />
                      </td>
                      <td className="num">{formatPacks(proposal.totalPacks)}</td>
                      <td className="num">
                        {proposal.thresholdPercent === null || proposal.thresholdPercent === undefined ?
                          <Badge tone="muted">Senza soglia</Badge>
                        : <Badge tone={proposal.thresholdPercent >= 100 ? 'ok' : 'warn'}>
                            {proposal.thresholdPercent}%
                          </Badge>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <GridSummary grid={grid} total={rows.length} />
          </div>
        )}
      </Card>
    </>
  )
}
